// Importaciones de React y librerías externas
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Rellax from 'rellax';

// Componentes MUI
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Componentes del kit (Minimals / Otis)
import MKBox from 'components/MKBox';
import MKTypography from 'components/MKTypography';

// Layout y recursos
import SimpleFooter from 'examples/Footers/SimpleFooter';
import footerRoutes from 'footer.routes';
import bgImage from 'assets/images/fondo5.png';
import logoblanco from 'assets/images/Logo Blanco.png';

// Secciones del formulario de postulación
import GeneralInfoForm from './sections/GeneralInfoForm';
import ResumeView from './sections/ResumeView';
import ExperienceModal from './sections/ExperienceModal';
import EducationModal from './sections/EducationModal';

// Utilidades: validaciones y servicio de envío
import {
  generalSchema,
  experienceSchema,
  educationSchema,
  validateData,
} from './utils/validations';

import { savePostulationToFirebase } from './utils/postulationService';

function Postulacion() {
  const headerRef = useRef(null);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Estado del Snackbar (feedback visual al usuario)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // --- Estado de datos del formulario ---

  const [formData, setFormData] = useState({
    nombres: '',
    apellidos: '',
    fecha: '',
    genero: '',
    email: '',
    telefono: '',
    departamento: '',
    municipio: '',
    profesion: '',
    oficio: '',
    habilidades: [],
  });

  const [resumeData, setResumeData] = useState({
    aboutMe: '',
    experiences: [],
    education: [],
    file: null,
  });

  // --- Estado de modales ---
  const [openExp, setOpenExp] = useState(false);
  const [openEdu, setOpenEdu] = useState(false);

  const [expForm, setExpForm] = useState({
    cargo: '',
    empresa: '',
    tipoEmpleo: '',
    modalidad: '',
    actualmente: false,
    fechaInicio: '',
    fechaFin: '',
    descripcion: '',
  });

  const [eduForm, setEduForm] = useState({
    institucion: '',
    titulo: '',
    disciplina: '',
    fechaInicio: '',
    fechaFin: '',
    descripcion: '',
  });

  // Efecto parallax del header
  useEffect(() => {
    if (headerRef.current) {
      const parallax = new Rellax(headerRef.current, { speed: -6 });
      return () => parallax.destroy();
    }
    return undefined;
  }, []);

  // --- Handlers de cambio ---

  const handleChange = (prop) => (event) => {
    const value = event.target ? event.target.value : event;
    setFormData({ ...formData, [prop]: value });
    if (errors[prop]) setErrors((prev) => ({ ...prev, [prop]: '' }));
  };

  const handleCloseModal = (setter) => {
    setter(false);
    setErrors({});
  };

  // --- Guardado local: Experiencia y Educación ---

  const saveExperience = async () => {
    const errs = await validateData(experienceSchema, expForm);
    if (Object.keys(errs).length > 0) return setErrors(errs);

    setResumeData((p) => ({
      ...p,
      experiences: [{ id: `${Date.now()}`, ...expForm }, ...p.experiences],
    }));

    setExpForm({
      cargo: '',
      empresa: '',
      tipoEmpleo: '',
      modalidad: '',
      actualmente: false,
      fechaInicio: '',
      fechaFin: '',
      descripcion: '',
    });
    handleCloseModal(setOpenExp);
  };

  const saveEducation = async () => {
    const errs = await validateData(educationSchema, eduForm);
    if (Object.keys(errs).length > 0) return setErrors(errs);

    setResumeData((p) => ({
      ...p,
      education: [{ id: `${Date.now()}`, ...eduForm }, ...p.education],
    }));

    setEduForm({
      institucion: '',
      titulo: '',
      disciplina: '',
      fechaInicio: '',
      fechaFin: '',
      descripcion: '',
    });
    handleCloseModal(setOpenEdu);
  };

  // --- Envío final a Firebase ---

  const handleGuardarTodo = async () => {
    // Validar información general antes de enviar
    const errs = await validateData(generalSchema, formData);

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setActiveTab(0);
      window.scrollTo({ top: 500, behavior: 'smooth' });
      return;
    }

    setLoading(true);

    try {
      const result = await savePostulationToFirebase(formData, resumeData);

      if (result.success) {
        // Feedback de éxito con Snackbar — bloquear envíos duplicados
        setSubmitted(true);
        setSnackbar({
          open: true,
          severity: 'success',
          message:
            'Postulación recibida correctamente.\nGracias por querer hacer parte de Dev Codes.\nNuestro equipo revisará tu perfil y pronto nos estaremos comunicando contigo para darte una respuesta.',
        });
        // Redirigir a la página principal después del mensaje
        setTimeout(() => navigate('/'), 4000);
      } else {
        // Feedback de error controlado con Snackbar
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'Hubo un error al guardar la postulación. Intenta nuevamente.',
        });
      }
    } catch (error) {
      console.error(error);
      // Feedback de error inesperado con Snackbar
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Error inesperado de conexión.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Cierre del Snackbar
  const handleCloseSnackbar = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  return (
    <>
      {/* Backdrop de carga durante el envío */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 9999,
          flexDirection: 'column',
          gap: 2,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
        <MKTypography variant="h6" color="white" fontWeight="regular">
          Enviando postulación…
        </MKTypography>
      </Backdrop>

      {/* Snackbar de feedback (éxito / error) */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', whiteSpace: 'pre-line' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header con parallax */}
      <MKBox
        ref={headerRef}
        minHeight="55vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            )}, url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'grid',
          placeItems: 'center',
          position: 'relative',
        }}
      >
        <MKBox
          component={Link}
          to="/"
          position="absolute"
          top={0}
          left={0}
          zIndex={10}
          width="100%"
          py={2}
          px={3}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <MKBox
            component="img"
            src={logoblanco}
            alt="DEV-CODES"
            width="135px"
            sx={{ maxHeight: '50px', cursor: 'pointer' }}
          />
        </MKBox>

        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            flexDirection="column"
            sx={{ mx: 'auto', textAlign: 'center', mb: '50px' }}
          >
            <MKTypography variant="h1" color="white">
              Trabaja con nosotros
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.8} mt={1}>
              Únete a nuestro equipo y desarrolla tu potencial.
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>

      {/* Contenido principal: formulario de postulación */}
      <Card
        sx={{
          p: { xs: 2, md: 3 },
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: '#ffffff',
          backdropFilter: 'saturate(200%) blur(30px)',
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
          borderRadius: 3,
        }}
      >
        <MKBox component="section" py={2}>
          <Container>
            <Grid container item justifyContent="center" xs={12} lg={8} mx="auto">
              <MKBox width="100%" mb={3} textAlign="left">
                <MKTypography variant="h4" color="info" fontWeight="bold" textTransform="uppercase">
                  REGISTRO
                </MKTypography>
                <MKTypography variant="body2" color="text">
                  Ingresa la información para poder continuar
                </MKTypography>
              </MKBox>

              {/* Tabs de navegación */}
              <Grid container item xs={12} mb={3} borderBottom="1px solid #e5e7eb">
                <MKBox display="flex" gap={4} pb={0.5} sx={{ width: '100%' }}>
                  {[
                    { label: 'Información general', icon: 'badge', id: 0 },
                    { label: 'Hoja de vida', icon: 'description', id: 1 },
                  ].map((tab) => (
                    <MKBox
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        pb: 1.2,
                        borderBottom:
                          activeTab === tab.id ? '2px solid #1a73e8' : '2px solid transparent',
                        color: activeTab === tab.id ? '#1a73e8' : '#6b7280',
                        opacity: activeTab === tab.id ? 1 : 0.8,
                      }}
                    >
                      <Icon sx={{ color: activeTab === tab.id ? '#1a73e8' : '#6b7280' }}>
                        {tab.icon}
                      </Icon>
                      <MKTypography
                        variant="button"
                        fontWeight={activeTab === tab.id ? 'bold' : 'regular'}
                        sx={{ color: activeTab === tab.id ? '#1a73e8' : '#6b7280' }}
                      >
                        {tab.label}
                      </MKTypography>
                    </MKBox>
                  ))}
                </MKBox>
              </Grid>

              {/* Contenido de la tab activa */}
              {activeTab === 0 ? (
                <GeneralInfoForm
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                  handleChange={handleChange}
                  setActiveTab={setActiveTab}
                />
              ) : (
                <ResumeView
                  resumeData={resumeData}
                  setResumeData={setResumeData}
                  setOpenExp={setOpenExp}
                  setOpenEdu={setOpenEdu}
                  handleDeleteExperience={(id) =>
                    setResumeData((p) => ({
                      ...p,
                      experiences: p.experiences.filter((e) => e.id !== id),
                    }))
                  }
                  handleDeleteEducation={(id) =>
                    setResumeData((p) => ({
                      ...p,
                      education: p.education.filter((e) => e.id !== id),
                    }))
                  }
                  handleGuardarTodo={handleGuardarTodo}
                  setActiveTab={setActiveTab}
                  loading={loading || submitted}
                />
              )}
            </Grid>
          </Container>
        </MKBox>
      </Card>

      {/* Modales de experiencia y educación */}
      <ExperienceModal
        open={openExp}
        handleClose={() => handleCloseModal(setOpenExp)}
        form={expForm}
        setForm={setExpForm}
        onSave={saveExperience}
        errors={errors}
      />

      <EducationModal
        open={openEdu}
        handleClose={() => handleCloseModal(setOpenEdu)}
        form={eduForm}
        setForm={setEduForm}
        onSave={saveEducation}
        errors={errors}
      />

      {/* Footer */}
      <MKBox pt={6} px={1} mt={6}>
        <SimpleFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default Postulacion;
